import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import CollapsibleFilterSection from '../common/CollapsibleFilterSection';
import ActionConfirmModal from '../common/ActionConfirmModal';
import UzDatePicker from '../common/UzDatePicker';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const expenseSections = [
  { key: 'incomingReagents', label: 'Kirim reaktivlar', accent: 'primary', statLabel: 'kirim', entity: 'incoming' },
  { key: 'outgoingReagents', label: 'Chiqim reaktivlar', accent: 'danger', statLabel: 'chiqim', entity: 'outgoing' },
  { key: 'products', label: 'Reaktivlar', accent: 'warning', statLabel: 'reaktiv', entity: 'product' },
  { key: 'properties', label: 'Xossalar', accent: 'info', statLabel: 'xossa', entity: 'property' },
  { key: 'methods', label: 'Metodlar', accent: 'success', statLabel: 'metod', entity: 'method' }
];

const today = () => new Date().toISOString().slice(0, 10);

const initialMovementForm = {
  id: null,
  product_id: '',
  quantity: '',
  comment: '',
  date: today()
};

const initialProductForm = {
  id: null,
  title: '',
  type_id: ''
};

const initialPropertyForm = {
  id: null,
  title: ''
};

const initialMethodForm = {
  id: null,
  title: '',
  lablist_id: '',
  is_active: 1,
  attachments: []
};

const formatNumber = value => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) {
    return String(value || 0);
  }

  const hasFraction = Math.abs(parsed % 1) > 0.0001;
  return parsed.toLocaleString('ru-RU', hasFraction ? { maximumFractionDigits: 2 } : undefined);
};

const attachmentPreview = attachments => {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return 'Biriktirilmagan';
  }

  return attachments
    .slice(0, 2)
    .map(item => `${item.product_title} (${item.quantity} ${item.type_title || 'dona'})`)
    .join(', ');
};

const movementModalTitle = entity => (entity === 'incoming' ? 'Kirim reaktiv' : 'Chiqim reaktiv');

const Expenses = () => {
  const [activeSectionKey, setActiveSectionKey] = useState('outgoingReagents');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [search, setSearch] = useState('');
  const [productFilterId, setProductFilterId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [incomingReagents, setIncomingReagents] = useState([]);
  const [outgoingReagents, setOutgoingReagents] = useState([]);
  const [products, setProducts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [methods, setMethods] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [lablistOptions, setLablistOptions] = useState([]);
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementEntity, setMovementEntity] = useState('outgoing');
  const [movementForm, setMovementForm] = useState(initialMovementForm);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productForm, setProductForm] = useState(initialProductForm);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [propertyForm, setPropertyForm] = useState(initialPropertyForm);
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [methodForm, setMethodForm] = useState(initialMethodForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState(null);

  const activeSection = expenseSections.find(section => section.key === activeSectionKey) || expenseSections[0];
  const isMovementSection = activeSectionKey === 'incomingReagents' || activeSectionKey === 'outgoingReagents';

  const fetchData = async () => {
    const res = await axios.get(`${apiBase}/expenses.php`, {
      params: {
        section: 'all',
        search: search.trim() || undefined,
        product_id: productFilterId || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        limit: 500
      }
    });

    if (res.data.success) {
      setIncomingReagents(res.data.incoming_reagents || []);
      setOutgoingReagents(res.data.outgoing_reagents || []);
      setProducts(res.data.products || []);
      setProperties(res.data.properties || []);
      setMethods(res.data.methods || []);
      setProductOptions(res.data.product_options || []);
      setTypeOptions(res.data.type_options || []);
      setLablistOptions(res.data.lablist_options || []);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData().catch(error => {
        toast.error(error.response?.data?.message || "Chiqim ma'lumotlari yuklanmadi");
      });
    }, 180);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, productFilterId, dateFrom, dateTo]);

  const counts = {
    incomingReagents: incomingReagents.length,
    outgoingReagents: outgoingReagents.length,
    products: products.length,
    properties: properties.length,
    methods: methods.length
  };

  const searchPlaceholder =
    activeSectionKey === 'incomingReagents'
      ? "Reaktiv yoki xossa bo'yicha qidirish"
      : activeSectionKey === 'outgoingReagents'
        ? "Reaktiv, izoh yoki xossa bo'yicha qidirish"
        : activeSectionKey === 'products'
          ? "Reaktiv yoki xossa bo'yicha qidirish"
          : activeSectionKey === 'properties'
            ? "Xossa nomi bo'yicha qidirish"
            : "Metod yoki analiz bo'yicha qidirish";

  const openMovementCreate = entity => {
    setMovementEntity(entity);
    setMovementForm({
      ...initialMovementForm,
      date: today()
    });
    setMovementModalOpen(true);
  };

  const openMovementEdit = (entity, item) => {
    setMovementEntity(entity);
    setMovementForm({
      id: item.id,
      product_id: item.product_id || '',
      quantity: item.quantity || '',
      comment: item.comment || '',
      date: item.date || today()
    });
    setMovementModalOpen(true);
  };

  const closeMovementModal = () => {
    setMovementModalOpen(false);
    setMovementForm(initialMovementForm);
  };

  const openProductCreate = () => {
    setProductForm(initialProductForm);
    setProductModalOpen(true);
  };

  const openProductEdit = item => {
    setProductForm({
      id: item.id,
      title: item.title || '',
      type_id: item.type_id || ''
    });
    setProductModalOpen(true);
  };

  const closeProductModal = () => {
    setProductModalOpen(false);
    setProductForm(initialProductForm);
  };

  const openPropertyCreate = () => {
    setPropertyForm(initialPropertyForm);
    setPropertyModalOpen(true);
  };

  const openPropertyEdit = item => {
    setPropertyForm({
      id: item.id,
      title: item.title || ''
    });
    setPropertyModalOpen(true);
  };

  const closePropertyModal = () => {
    setPropertyModalOpen(false);
    setPropertyForm(initialPropertyForm);
  };

  const openMethodCreate = () => {
    setMethodForm(initialMethodForm);
    setMethodModalOpen(true);
  };

  const openMethodEdit = item => {
    setMethodForm({
      id: item.id,
      title: item.title || '',
      lablist_id: item.lablist_id || '',
      is_active: item.is_active ? 1 : 0,
      attachments: (item.attachments || []).map(attachment => ({
        product_id: Number(attachment.product_id),
        quantity: attachment.quantity || ''
      }))
    });
    setMethodModalOpen(true);
  };

  const closeMethodModal = () => {
    setMethodModalOpen(false);
    setMethodForm(initialMethodForm);
  };

  const getMethodAttachmentQuantity = productId => {
    const found = methodForm.attachments.find(item => Number(item.product_id) === Number(productId));
    return found ? found.quantity : '';
  };

  const updateMethodAttachment = (productId, quantity) => {
    setMethodForm(form => {
      const attachments = [...form.attachments];
      const index = attachments.findIndex(item => Number(item.product_id) === Number(productId));
      const nextQuantity = String(quantity);

      if (nextQuantity.trim() === '') {
        if (index >= 0) {
          attachments.splice(index, 1);
        }
      } else if (index >= 0) {
        attachments[index] = { ...attachments[index], quantity: nextQuantity };
      } else {
        attachments.push({ product_id: Number(productId), quantity: nextQuantity });
      }

      return {
        ...form,
        attachments
      };
    });
  };

  const handleMovementSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: movementEntity,
        product_id: Number(movementForm.product_id),
        quantity: movementForm.quantity,
        comment: movementForm.comment,
        date: movementForm.date
      };

      if (movementForm.id) {
        await axios.put(`${apiBase}/expenses.php`, { ...payload, id: movementForm.id });
        toast.success('Yozuv yangilandi');
      } else {
        await axios.post(`${apiBase}/expenses.php`, payload);
        toast.success(`${movementModalTitle(movementEntity)} yozuvi qo'shildi`);
      }

      closeMovementModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Reaktiv yozuvini saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleProductSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: 'product',
        title: productForm.title.trim(),
        type_id: Number(productForm.type_id)
      };

      if (productForm.id) {
        await axios.put(`${apiBase}/expenses.php`, { ...payload, id: productForm.id });
        toast.success('Reaktiv yangilandi');
      } else {
        await axios.post(`${apiBase}/expenses.php`, payload);
        toast.success("Reaktiv qo'shildi");
      }

      closeProductModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Reaktivni saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePropertySubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: 'property',
        title: propertyForm.title.trim()
      };

      if (propertyForm.id) {
        await axios.put(`${apiBase}/expenses.php`, { ...payload, id: propertyForm.id });
        toast.success('Xossa yangilandi');
      } else {
        await axios.post(`${apiBase}/expenses.php`, payload);
        toast.success("Xossa qo'shildi");
      }

      closePropertyModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Xossani saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMethodSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    try {
      const payload = {
        entity: 'method',
        title: methodForm.title.trim(),
        lablist_id: Number(methodForm.lablist_id),
        is_active: Number(methodForm.is_active),
        attachments: methodForm.attachments
          .filter(item => String(item.quantity).trim() !== '')
          .map(item => ({
            product_id: Number(item.product_id),
            quantity: item.quantity
          }))
      };

      if (methodForm.id) {
        await axios.put(`${apiBase}/expenses.php`, { ...payload, id: methodForm.id });
        toast.success('Metod yangilandi');
      } else {
        await axios.post(`${apiBase}/expenses.php`, payload);
        toast.success("Metod qo'shildi");
      }

      closeMethodModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Metodni saqlashda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || isSaving) return;

    setIsSaving(true);
    try {
      const res = await axios.delete(`${apiBase}/expenses.php`, {
        data: {
          entity: deleteTarget.entity,
          id: deleteTarget.id
        }
      });

      toast.success(res.data?.message || "Yozuv o'chirildi");
      setDeleteTarget(null);
      if (deleteTarget.entity === 'product') closeProductModal();
      if (deleteTarget.entity === 'property') closePropertyModal();
      if (deleteTarget.entity === 'incoming' || deleteTarget.entity === 'outgoing') closeMovementModal();
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Yozuvni o'chirishda xatolik yuz berdi");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMethodStatusToggle = async item => {
    if (statusChangingId) return;

    setStatusChangingId(item.id);
    try {
      const res = await axios.put(`${apiBase}/expenses.php`, {
        entity: 'method',
        id: item.id,
        title: item.title,
        lablist_id: item.lablist_id,
        is_active: item.is_active ? 0 : 1,
        attachments: (item.attachments || []).map(attachment => ({
          product_id: Number(attachment.product_id),
          quantity: attachment.quantity
        }))
      });

      toast.success(res.data?.message || 'Metod holati yangilandi');
      fetchData().catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Metod holatini yangilashda xatolik yuz berdi');
    } finally {
      setStatusChangingId(null);
    }
  };

  const resetMovementFilters = () => {
    setProductFilterId('');
    setDateFrom('');
    setDateTo('');
  };

  const renderAddButton = () => {
    if (activeSectionKey === 'incomingReagents') {
      return (
        <Button type="button" color="outline-primary" className="med-outline-add-btn" onClick={() => openMovementCreate('incoming')}>
          <FontAwesomeIcon icon="plus" className="mr-2" />
          Yangi kirim
        </Button>
      );
    }

    if (activeSectionKey === 'outgoingReagents') {
      return (
        <Button type="button" color="outline-danger" className="med-outline-add-btn" onClick={() => openMovementCreate('outgoing')}>
          <FontAwesomeIcon icon="plus" className="mr-2" />
          Yangi chiqim
        </Button>
      );
    }

    if (activeSectionKey === 'products') {
      return (
        <Button type="button" color="outline-warning" className="med-outline-add-btn" onClick={openProductCreate}>
          <FontAwesomeIcon icon="plus" className="mr-2" />
          Yangi reaktiv
        </Button>
      );
    }

    if (activeSectionKey === 'properties') {
      return (
        <Button type="button" color="outline-info" className="med-outline-add-btn" onClick={openPropertyCreate}>
          <FontAwesomeIcon icon="plus" className="mr-2" />
          Yangi xossa
        </Button>
      );
    }

    return (
      <Button type="button" color="outline-success" className="med-outline-add-btn" onClick={openMethodCreate}>
        <FontAwesomeIcon icon="plus" className="mr-2" />
        Yangi metod
      </Button>
    );
  };

  const incomingColumns = [
    {
      dataField: 'product_title',
      text: 'Reaktiv',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <>
          <div className="font-weight-bold">{value || '-'}</div>
          <small className="text-500">{item.type_title || '-'}</small>
        </>
      )
    },
    {
      dataField: 'quantity',
      text: 'Miqdor',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => `${formatNumber(value)} ${item.type_title || ''}`.trim()
    },
    { dataField: 'date', text: 'Sana', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openMovementEdit('incoming', item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ entity: 'incoming', id: item.id, name: item.product_title || 'Yozuv' })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const outgoingColumns = [
    ...incomingColumns.slice(0, 2),
    { dataField: 'comment', text: 'Izoh', sort: true, classes: 'align-middle text-600', formatter: value => value || '-' },
    { dataField: 'date', text: 'Sana', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openMovementEdit('outgoing', item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ entity: 'outgoing', id: item.id, name: item.product_title || 'Yozuv' })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const productColumns = [
    { dataField: 'title', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    {
      dataField: 'stock_quantity',
      text: 'Ombordagi qoldiq',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <>
          <div className="font-weight-semi-bold">{formatNumber(value)}</div>
          <small className="text-500">
            Kirim: {formatNumber(item.incoming_quantity)} / Chiqim: {formatNumber(item.outgoing_quantity)}
          </small>
        </>
      )
    },
    { dataField: 'type_title', text: 'Xossa', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openProductEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ entity: 'product', id: item.id, name: item.title })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const propertyColumns = [
    { dataField: 'title', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'product_count', text: 'Reaktivlar soni', sort: true, classes: 'align-middle' },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openPropertyEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className="p-0 med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ entity: 'property', id: item.id, name: item.title })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const methodColumns = [
    { dataField: 'title', text: 'Nomi', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'analysis_title', text: 'Analiz', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'attachments_count',
      text: 'Biriktirilgan reaktivlar',
      sort: true,
      classes: 'align-middle',
      formatter: (value, item) => (
        <>
          <div className="text-600">{attachmentPreview(item.attachments)}</div>
          <small className="text-500">{value || 0} ta reaktiv</small>
        </>
      )
    },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openMethodEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            type="button"
            color="link"
            className={`p-0 med-action-btn med-action-btn-toggle${item.is_active ? '' : ' is-inactive'}`}
            onClick={() => handleMethodStatusToggle(item)}
            disabled={statusChangingId === item.id}
            title={item.is_active ? 'Nofaol qilish' : 'Faol qilish'}
          >
            <FontAwesomeIcon
              icon={statusChangingId === item.id ? 'spinner' : item.is_active ? 'ban' : 'check-circle'}
              spin={statusChangingId === item.id}
            />
          </Button>
        </div>
      )
    }
  ];

  return (
    <>
      <PageHeader
        title="Chiqim"
        description="Eski chiqim sahifalaridagi kirim, chiqim, reaktiv, xossa va metod jadvallari shu bo'limda birlashtirildi."
        className="mb-3"
      />

      <Card className="border-0 shadow-sm med-dashboard-card med-patient-filter-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Chiqim bo'limlari"
            summary={`${activeSection.label} bo'limi tanlangan`}
            actions={
              <Badge color={`soft-${activeSection.accent}`} pill>
                {activeSection.label}: {counts[activeSection.key] || 0}
              </Badge>
            }
            storageKey="expenses-sections-collapse"
          >
            <div className="med-patient-filter-grid med-expenses-filter-grid">
              {expenseSections.map(section => (
                <button
                  key={section.key}
                  type="button"
                  className={`med-patient-filter-item med-patient-filter-item-${section.accent}${
                    activeSectionKey === section.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveSectionKey(section.key)}
                >
                  <span className="med-patient-filter-label">{section.label}</span>
                  <span className="med-patient-filter-meta">
                    <strong>{counts[section.key] || 0}</strong>
                    <small>{section.statLabel}</small>
                  </span>
                </button>
              ))}
            </div>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Jadval filtrlari"
            summary={search ? `Qidiruv: ${search}` : `${activeSection.label} bo'yicha joriy holat`}
            storageKey={`expenses-filters-${activeSectionKey}`}
          >
            <Row className="mb-3 align-items-center">
              <Col md="auto" className="mb-3 mb-md-0">
                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
              </Col>
              <Col md={isMovementSection ? 3 : 5} className="mb-3 mb-md-0">
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="bg-200 border-0"
                />
              </Col>
              {isMovementSection && (
                <>
                  <Col md={2} className="mb-3 mb-md-0">
                    <Input
                      type="select"
                      value={productFilterId}
                      onChange={e => setProductFilterId(e.target.value)}
                      className="bg-200 border-0"
                    >
                      <option value="">Barcha reaktivlar</option>
                      {productOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.title}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2} className="mb-3 mb-md-0">
                    <UzDatePicker value={dateFrom} onChange={setDateFrom} className="form-control bg-200 border-0" placeholder="dan sana" />
                  </Col>
                  <Col md={2} className="mb-3 mb-md-0">
                    <UzDatePicker value={dateTo} onChange={setDateTo} className="form-control bg-200 border-0" placeholder="gacha sana" />
                  </Col>
                </>
              )}
              <Col className="text-md-right">
                <div className="d-flex flex-wrap justify-content-md-end align-items-center">
                  {isMovementSection && (productFilterId || dateFrom || dateTo) && (
                    <Button type="button" color="light" className="mr-2 mb-2 mb-md-0" onClick={resetMovementFilters}>
                      Filterni tozalash
                    </Button>
                  )}
                  {renderAddButton()}
                </div>
              </Col>
            </Row>
          </CollapsibleFilterSection>

          {activeSectionKey === 'incomingReagents' && (
            <MedDataTable
              data={incomingReagents}
              columns={incomingColumns}
              pageSize={pageSize}
              paginationResetKey={`incoming-${search}-${productFilterId}-${dateFrom}-${dateTo}`}
              noDataIndication={<div className="med-dashboard-empty">Kirim reaktiv yozuvlari topilmadi.</div>}
            />
          )}

          {activeSectionKey === 'outgoingReagents' && (
            <MedDataTable
              data={outgoingReagents}
              columns={outgoingColumns}
              pageSize={pageSize}
              paginationResetKey={`outgoing-${search}-${productFilterId}-${dateFrom}-${dateTo}`}
              noDataIndication={<div className="med-dashboard-empty">Chiqim reaktiv yozuvlari topilmadi.</div>}
            />
          )}
          {activeSectionKey === 'products' && (
            <MedDataTable
              data={products}
              columns={productColumns}
              pageSize={pageSize}
              paginationResetKey={`products-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Reaktivlar topilmadi.</div>}
            />
          )}

          {activeSectionKey === 'properties' && (
            <MedDataTable
              data={properties}
              columns={propertyColumns}
              pageSize={pageSize}
              paginationResetKey={`properties-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Xossalar topilmadi.</div>}
            />
          )}

          {activeSectionKey === 'methods' && (
            <MedDataTable
              data={methods}
              columns={methodColumns}
              pageSize={pageSize}
              paginationResetKey={`methods-${search}`}
              noDataIndication={<div className="med-dashboard-empty">Metodlar topilmadi.</div>}
            />
          )}
        </CardBody>
      </Card>

      <Modal isOpen={movementModalOpen} toggle={closeMovementModal} centered className="med-confirm-modal">
        <ModalHeader toggle={closeMovementModal}>
          {movementForm.id ? `${movementModalTitle(movementEntity)} yozuvini tahrirlash` : `${movementModalTitle(movementEntity)} yozuvi`}
        </ModalHeader>
        <Form onSubmit={handleMovementSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Reaktiv</Label>
              <Input
                type="select"
                value={movementForm.product_id}
                onChange={e => {
                  const { value } = e.target;
                  setMovementForm(form => ({ ...form, product_id: value }));
                }}
                className="bg-200 border-0"
              >
                <option value="">Tanlang</option>
                {productOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                    {option.type_title ? ` (${option.type_title})` : ''}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label>Miqdor</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={movementForm.quantity}
                    onChange={e => {
                      const { value } = e.target;
                      setMovementForm(form => ({ ...form, quantity: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Sana</Label>
                  <UzDatePicker
                    value={movementForm.date}
                    onChange={value => setMovementForm(form => ({ ...form, date: value }))}
                    className="form-control bg-200 border-0"
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Izoh</Label>
              <Input
                type="textarea"
                rows="4"
                value={movementForm.comment}
                onChange={e => {
                  const { value } = e.target;
                  setMovementForm(form => ({ ...form, comment: value }));
                }}
                className="bg-200 border-0"
                placeholder="Qo'shimcha izoh"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeMovementModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : movementForm.id ? 'check-circle' : 'plus'} spin={isSaving} className="mr-2" />
              {movementForm.id ? 'Yangilash' : 'Saqlash'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={productModalOpen} toggle={closeProductModal} centered className="med-confirm-modal">
        <ModalHeader toggle={closeProductModal}>{productForm.id ? 'Reaktivni tahrirlash' : 'Yangi reaktiv'}</ModalHeader>
        <Form onSubmit={handleProductSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Reaktiv nomi</Label>
              <Input
                value={productForm.title}
                onChange={e => {
                  const { value } = e.target;
                  setProductForm(form => ({ ...form, title: value }));
                }}
                className="bg-200 border-0"
              />
            </FormGroup>
            <FormGroup>
              <Label>Xossa</Label>
              <Input
                type="select"
                value={productForm.type_id}
                onChange={e => {
                  const { value } = e.target;
                  setProductForm(form => ({ ...form, type_id: value }));
                }}
                className="bg-200 border-0"
              >
                <option value="">Tanlang</option>
                {typeOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeProductModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : productForm.id ? 'check-circle' : 'plus'} spin={isSaving} className="mr-2" />
              {productForm.id ? 'Yangilash' : 'Saqlash'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={propertyModalOpen} toggle={closePropertyModal} centered className="med-confirm-modal">
        <ModalHeader toggle={closePropertyModal}>{propertyForm.id ? 'Xossani tahrirlash' : 'Yangi xossa'}</ModalHeader>
        <Form onSubmit={handlePropertySubmit}>
          <ModalBody>
            <FormGroup className="mb-0">
              <Label>Xossa nomi</Label>
              <Input
                value={propertyForm.title}
                onChange={e => {
                  const { value } = e.target;
                  setPropertyForm(form => ({ ...form, title: value }));
                }}
                className="bg-200 border-0"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closePropertyModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : propertyForm.id ? 'check-circle' : 'plus'} spin={isSaving} className="mr-2" />
              {propertyForm.id ? 'Yangilash' : 'Saqlash'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={methodModalOpen} toggle={closeMethodModal} size="lg" centered className="med-confirm-modal">
        <ModalHeader toggle={closeMethodModal}>{methodForm.id ? 'Metodni tahrirlash' : 'Yangi metod'}</ModalHeader>
        <Form onSubmit={handleMethodSubmit}>
          <ModalBody>
            <Row form>
              <Col md={7}>
                <FormGroup>
                  <Label>Metod nomi</Label>
                  <Input
                    value={methodForm.title}
                    onChange={e => {
                      const { value } = e.target;
                      setMethodForm(form => ({ ...form, title: value }));
                    }}
                    className="bg-200 border-0"
                  />
                </FormGroup>
              </Col>
              <Col md={5}>
                <FormGroup>
                  <Label>Holat</Label>
                  <Input
                    type="select"
                    value={methodForm.is_active}
                    onChange={e => {
                      const { value } = e.target;
                      setMethodForm(form => ({ ...form, is_active: Number(value) }));
                    }}
                    className="bg-200 border-0"
                  >
                    <option value={1}>Faol</option>
                    <option value={0}>Nofaol</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Analiz</Label>
              <Input
                type="select"
                value={methodForm.lablist_id}
                onChange={e => {
                  const { value } = e.target;
                  setMethodForm(form => ({ ...form, lablist_id: value }));
                }}
                className="bg-200 border-0"
              >
                <option value="">Tanlang</option>
                {lablistOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <div>
                <h6 className="mb-1">Biriktirilgan reaktivlar</h6>
                <small className="text-500">Har bir reaktiv uchun kerakli miqdorni kiriting.</small>
              </div>
              <Badge color="soft-success" pill>
                Tanlandi: {methodForm.attachments.length}
              </Badge>
            </div>

            <div className="med-expense-attachment-editor">
              {productOptions.map(option => (
                <div key={option.id} className="med-expense-attachment-row">
                  <div className="med-expense-attachment-copy">
                    <div className="font-weight-semi-bold">{option.title}</div>
                    <small className="text-500">{option.type_title || "O'lchov ko'rsatilmagan"}</small>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getMethodAttachmentQuantity(option.id)}
                    onChange={e => updateMethodAttachment(option.id, e.target.value)}
                    className="bg-200 border-0"
                    placeholder="0"
                  />
                </div>
              ))}
              {productOptions.length === 0 && <div className="med-dashboard-empty">Reaktivlar topilmadi.</div>}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeMethodModal}>
              Bekor qilish
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              <FontAwesomeIcon icon={isSaving ? 'spinner' : methodForm.id ? 'check-circle' : 'plus'} spin={isSaving} className="mr-2" />
              {methodForm.id ? 'Yangilash' : 'Saqlash'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Elementni o'chirish"
        message={deleteTarget ? `${deleteTarget.name} elementini o'chirmoqchimisiz?` : ''}
        confirmLabel="O'chirish"
        onConfirm={handleDelete}
      />
    </>
  );
};

export default Expenses;
